import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { CalendarDays, Check, ClipboardList, FileText, KeyRound, MessageSquare, Sparkles, User } from 'lucide-react';
import { InspectorFilesBox } from './InspectorFilesBox';
import { InspectorNotesFeed, type InspectorNote } from './InspectorNotesFeed';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';
import { useI18n } from '../lib/i18n';
import { ScheduleModal } from './ScheduleModal';
import { MentionTextarea } from './MentionTextarea';
import { renderMentions } from '../lib/mentions';

/**
 * Paneles de scheduling del claim (réplica de ClaimRoute): próxima inspección + clima,
 * Field Adjuster, Notes for the inspector, Monday updates (leer/escribir + draft AI) y
 * Scheduling activity.
 */
type NextVisit = { appointment_id: string; scheduled_start: string; inspector_name: string | null; contractor_name: string | null; type: string; weather: { icon: string; short: string } | null };
type Info = { adjusterInfo: string; inspectorNotes: string; inspectorNoteEntries?: InspectorNote[]; gateCode?: string; nextVisit?: NextVisit | null };
type Update = { id?: string; body?: string; text?: string; author_name?: string; created_at?: string; at?: string };
type Activity = { kind: string; id: string; at: string | null; label: string; sub?: string; by?: string | null; reportId?: string };

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

export function ClaimSchedulingExtras({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [info, setInfo] = useState<Info>({ adjusterInfo: '', inspectorNotes: '', nextVisit: null });
  const [resched, setResched] = useState(false);
  const [reschedUrgent, setReschedUrgent] = useState(false);
  const [gate, setGate] = useState('');
  const [activity, setActivity] = useState<Activity[]>([]);
  const [updates, setUpdates] = useState<Update[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState('');
  const [saved, setSaved] = useState('');

  const { fallo, marcar, limpiar } = useFallo();
  function load() {
    api.get<Info>(`/api/claims/${claimId}/scheduling-info`).then((r) => { limpiar(); setInfo(r); setGate(r.gateCode ?? ''); }).catch(marcar);
    api.get<{ activity: Activity[] }>(`/api/claims/${claimId}/scheduling-activity`).then((r) => setActivity(r.activity)).catch(() => {});
    api.get<{ updates: Update[] }>(`/api/claims/${claimId}/updates`).then((r) => setUpdates(r.updates || [])).catch(() => {});
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [claimId]);

  async function saveGate() { setBusy('gate'); try { await api.patch(`/api/claims/${claimId}/gate-code`, { text: gate }); setInfo({ ...info, gateCode: gate }); setSaved('gate'); setTimeout(() => setSaved(''), 2000); } catch { /* */ } finally { setBusy(''); } }
  async function genDraft() { setBusy('draft'); try { const r = await api.post<{ draft: string }>(`/api/claims/${claimId}/homeowner-draft`, {}); setDraft(r.draft); } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(''); } }
  async function postUpdate() { const text = draft.trim(); if (!text) return; setBusy('post'); try { await api.post(`/api/claims/${claimId}/updates`, { text }); setDraft(''); load(); } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(''); } }

  const box: React.CSSProperties = { marginTop: 6 };

  const nv = info.nextVisit;
  const nvWhen = (iso: string) => new Date(iso).toLocaleString(localeUI(), { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {fallo && <NoCargo onRetry={load} compact />}
      {/* (Los botones de Request a visit se movieron ARRIBA del panel de Appointments, con su historial.) */}
      {/* Próxima inspección + clima (port del header de ClaimRoute) */}
      {nv && (
        <div className="card" style={{ borderLeft: '3px solid #2f5fb0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('cr.nextInspection')}</div>
              <div style={{ fontSize: 'var(--fs-base)', fontWeight: 'var(--fw-bold)', marginTop: 2 }}><CalendarDays size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {nvWhen(nv.scheduled_start)}</div>
              <div className="muted" style={{ fontSize: 'var(--fs-sm)', marginTop: 2 }}>
                <User size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {nv.inspector_name || nv.contractor_name || t('cr.unassigned')}
                {nv.weather && <span> · {nv.weather.icon} {nv.weather.short}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="btn sm" onClick={() => { setReschedUrgent(false); setResched(true); }}>⟳ {t('sch.rescheduleShort')}</button>
              <button className="btn sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} title={t('sch.urgentHint')} onClick={() => { setReschedUrgent(true); setResched(true); }}>🚨 {t('sch.urgentReschedule')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Field Adjuster: se quitó de aquí — se maneja con el Adjuster Info del card (junto a la foto del techo). */}

      {/* Notes for the inspector */}
      <div className="card">
        <h2 className="card-title"><ClipboardList size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cr.inspectorNotesTitle')}</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 'var(--fs-sm)' }}>{t('cr.inspectorNotesHelp')}</p>
        {/* Lista de notas con autor y hora (Manuel 2026-10-08): cada Save agrega una y se ve que se envió. */}
        <InspectorNotesFeed claimId={claimId} initial={info.inspectorNoteEntries ?? null} mono />
        {/* La foto de referencia del ATR va ACÁ, pegada a la nota que la menciona. Antes esto era
            solo texto: escribían "te dejo la foto" y no había dónde soltarla. */}
        <div style={{ marginTop: 10 }}><InspectorFilesBox claimId={claimId} /></div>
      </div>

      {/* Gate / community access code */}
      <div className="card">
        <h2 className="card-title"><KeyRound size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cci.gateCode')}</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 'var(--fs-sm)' }}>{t('sch.gateHelp')}</p>
        <input className="input" style={{ width: '100%', fontFamily: 'var(--font-mono)', letterSpacing: 1 }} value={gate} onChange={(e) => setGate(e.target.value)} placeholder={t('cci.gateCodeHint')} />
        <div style={box}><button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }} disabled={busy === 'gate' || gate === (info.gateCode ?? '')} onClick={saveGate}>{t('common.save')}</button>{saved === 'gate' && <span style={{ marginLeft: 8, color: 'var(--green)', fontSize: 'var(--fs-2xs)' }}><Check size={12} strokeWidth={1.75} /></span>}</div>
      </div>

      {/* Monday updates */}
      <div className="card">
        <h2 className="card-title"><MessageSquare size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cr.mondayUpdates')}</h2>
        <p className="muted" style={{ marginTop: 0, fontSize: 'var(--fs-sm)' }}>{t('cr.mondayUpdatesHelp')}</p>
        <MentionTextarea value={draft} onChange={setDraft} placeholder={t('cr.mondayUpdatesPh')} minHeight={70} />
        <div style={{ ...box, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn ghost sm" disabled={busy === 'draft'} onClick={genDraft}>{busy === 'draft' ? '…' : <><Sparkles size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cr.draftUpdate')}</>}</button>
          <button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }} disabled={busy === 'post' || !draft.trim()} onClick={postUpdate}>{t('cr.postToMonday')}</button>
        </div>
        {updates.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', display: 'grid', gap: 8 }}>
            {updates.slice(0, 8).map((u, i) => (
              <li key={u.id ?? i} style={{ background: 'var(--bg)', borderRadius: 8, padding: '8px 10px', fontSize: 'var(--fs-sm)' }}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{renderMentions(u.body ?? u.text ?? '')}</div>
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 3 }}>{u.author_name || '—'} · {when(u.created_at ?? u.at)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Scheduling activity */}
      <div className="card">
        <h2 className="card-title"><CalendarDays size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cr.schedulingActivity')}</h2>
        {activity.length === 0 ? <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>{t('cr.noSchedulingActions')}</p>
          : <div style={{ display: 'grid', gap: 6 }}>
            {activity.map((a) => (
              <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', borderBottom: '1px solid var(--line)', paddingBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--fs-sm)' }}>{a.label}{a.sub ? <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}> · {a.sub}</span> : null}</div>
                  {/* Quién lo agendó (estilo ClaimRoute) + link al inspection report si existe. */}
                  <div className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {a.by && <span>{t('cr.by')} {a.by}</span>}
                    {a.reportId && <Link to={`?report=${a.reportId}`} style={{ color: 'var(--accent)' }}><FileText size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.inspectorReport')}</Link>}
                  </div>
                </div>
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>{when(a.at)}</span>
              </div>
            ))}
          </div>}
      </div>

      {resched && nv && (
        <ScheduleModal claimId={claimId} appointmentId={nv.appointment_id} mode="reschedule" urgent={reschedUrgent}
          onClose={() => setResched(false)} onDone={() => { setResched(false); load(); }} />
      )}
    </div>
  );
}
